/**
 * Report list table showing generated reports with download links.
 */

import DataTable, { type Column } from '@/components/common/DataTable';
import { formatDate, formatFileSize } from '@/utils/formatters';
import { Download, Trash2 } from 'lucide-react';
import type { Report } from '@/types/report';

interface ReportListProps {
  reports: Report[];
  onDownload: (report: Report) => void;
  onDelete: (report: Report) => void;
  canManage?: boolean;
}

/** Status badge for report generation. */
function reportStatusBadge(status: string) {
  const colors: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-600',
    generating: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${colors[status] ?? 'bg-gray-100'}`}>
      {status}
    </span>
  );
}

export default function ReportList({ reports, onDownload, onDelete,canManage=true }: ReportListProps) {
  const columns: Column<Report>[] = [
    { key: 'name', header: 'Report Name', sortable: true,render:(item:Report)=><div>{item.name}
      {item.comparison_summary&&<p className="mt-1 text-xs text-gray-500">New: {item.comparison_summary.new} · Persistent: {item.comparison_summary.persistent} · No longer detected: {item.comparison_summary.resolved} · Not retested: {item.comparison_summary.not_retested}</p>}
      {item.error_message&&<p className="mt-1 text-xs text-red-700">{item.error_message}</p>}
    </div> },
    {
      key: 'report_type',
      header: 'Type',
      render: (item: Report) => (
        <span className="text-xs capitalize">{item.report_type.replace('_', ' ')}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (item: Report) => reportStatusBadge(item.status),
    },
    {
      key: 'file_size',
      header: 'Size',
      render: (item: Report) => formatFileSize(item.file_size),
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: true,
      render: (item: Report) => <span className="text-xs">{formatDate(item.created_at)}</span>,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-20',
      render: (item: Report) => (
        <div className="flex items-center gap-1">
          {item.status === 'completed' && (
            <button
              onClick={(e) => { e.stopPropagation(); onDownload(item); }}
              className="rounded p-1 text-indigo-600 hover:bg-indigo-50"
              title="Download report"
            >
              <Download className="h-4 w-4" />
            </button>
          )}
          {canManage&&<button
            onClick={(e) => { e.stopPropagation(); onDelete(item); }}
            className="rounded p-1 text-red-500 hover:bg-red-50"
            title="Delete report"
          >
            <Trash2 className="h-4 w-4" />
          </button>}
        </div>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={reports}
      rowKey={(item) => item.id}
      emptyMessage="No reports generated yet."
    />
  );
}
